// src/components/LaptopSlider.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Api from '../Api';
import { getProductDetailsPath } from '../helpers/navigation';
import './LaptopSlider.css';

const LaptopDisplay = ({ onLoaded }) => {
  const [products, setProducts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [hoverImageIndexes, setHoverImageIndexes] = useState({});
  const displayCount   = 6;
  const fetchCount     = 30;
  const navigate       = useNavigate();
  const hoverIntervals = useRef({});
  const rotateInterval = useRef(null);
  const CATEGORY_ID    = 4;

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLaptops = async () => {
      try {
        const response = await Api.get(`products/?category_id=${CATEGORY_ID}`);
        const data     = Array.isArray(response.data) ? response.data : [];
        if (isMounted) {
          const filtered = data.filter((p) => {
            const id = p.category_id ?? p.category?.category_id ?? p.category?.id ?? null;
            return id === CATEGORY_ID;
          });
          setProducts(shuffleArray(filtered.slice(0, fetchCount)));
        }
      } catch {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) {
          setLoading(false);
          if (typeof onLoaded === 'function') onLoaded();
        }
      }
    };

    fetchLaptops();

    rotateInterval.current = setInterval(() => {
      setProducts((prev) => {
        if (!prev || prev.length === 0) return prev;
        return [...prev.slice(1), prev[0]];
      });
    }, 12000);

    return () => {
      isMounted = false;
      clearInterval(rotateInterval.current);
      Object.values(hoverIntervals.current).forEach(clearInterval);
    };
  }, [onLoaded]);

  const handleProductClick = (product) => navigate(getProductDetailsPath(product));

  const handleMouseEnter = (id, images) => {
    if (!images || images.length < 2) return;
    let idx = 0;
    if (hoverIntervals.current[id]) clearInterval(hoverIntervals.current[id]);
    hoverIntervals.current[id] = setInterval(() => {
      idx = (idx + 1) % images.length;
      setHoverImageIndexes((prev) => ({ ...prev, [id]: idx }));
    }, 1000);
  };

  const handleMouseLeave = (id) => {
    if (hoverIntervals.current[id]) {
      clearInterval(hoverIntervals.current[id]);
      hoverIntervals.current[id] = null;
    }
    setHoverImageIndexes((prev) => ({ ...prev, [id]: 0 }));
  };

  if (loading) return null;

  return (
    <div className="laptop-container">
      <h2>Featured Laptops</h2>
      <div className="laptop-display">
        {products.length === 0 ? (
          <p>No laptops available at the moment.</p>
        ) : (
          /* ✅ NO inline style here — CSS handles all scroll behaviour */
          <div className="laptop-slider">
            {products.slice(0, displayCount).map((laptop) => {
              if (!laptop) return null;
              const images = [
                laptop.image_urls?.medium,
                laptop.secondary_image_urls?.medium,
                laptop.tertiary_image_urls?.medium,
                laptop.quaternary_image_urls?.medium,
              ].filter(Boolean);
              const currentImg = images[hoverImageIndexes[laptop.product_id] || 0] || images[0] || '/placeholder.jpg';

              return (
                <div
                  key={laptop.product_id}
                  className="laptop-item"
                  onClick={() => handleProductClick(laptop)}
                  onMouseEnter={() => handleMouseEnter(laptop.product_id, images)}
                  onMouseLeave={() => handleMouseLeave(laptop.product_id)}
                >
                  <img
                    src={currentImg}
                    alt={laptop.name || 'Laptop'}
                    className="laptop-image"
                    onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                    loading="lazy"
                  />
                  <h3 className="laptop-name">{laptop.name}</h3>
                  <p className="laptop-price">
                    {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(parseFloat(laptop.price) || 0)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LaptopDisplay;